import { emissionFactorSchema } from '@app/components/food-modelling/schema';
import {
  ProductIngredientEntityTypeEnum,
  SoldGoodModelledAsEnum,
} from '@shared/api/types';
import { nativeEnum, object, string, z } from 'zod';

export const soldItemFormSchema = object({
  remoteId: string().optional(),
  name: string().min(1),

  unit: string().min(1).optional(),
  itemsPerPack: z.coerce.number().min(0.001).optional(),
  itemWeight: z.coerce.number().min(0.001).optional(),
  inputUnit: string().optional(),

  modelledAs: nativeEnum(SoldGoodModelledAsEnum).optional(),

  basicFoodUuid: string().optional(),
  linkedItemUuid: string().optional(),

  emissionFactors: object({
    farming: emissionFactorSchema.optional(),
    processing: emissionFactorSchema.optional(),
    packaging: emissionFactorSchema.optional().nullish(),
    transport: emissionFactorSchema.optional(),
  }).optional(),

  recipeDescription: string().optional(),

  ingredients: z
    .array(
      z.object({
        entityUuid: z.string().nullish(),
        entityType: z.nativeEnum(ProductIngredientEntityTypeEnum),
        quantity: z.coerce.number(),
        unitUuid: z.string().nullish(),
      })
    )
    .optional(),
}).superRefine((data, ctx) => {
  if (data.modelledAs === SoldGoodModelledAsEnum.basic_food) {
    if (!data.basicFoodUuid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please select a matched food',
        path: ['basicFoodUuid'],
      });
    }

    // If any emissionFactors are set to method custom, ensure source is provided
    if (data.emissionFactors) {
      for (const [stage, factor] of Object.entries(data.emissionFactors)) {
        // Validate that value is provided and is a valid number
        if (
          factor?.value === undefined ||
          factor?.value === null ||
          factor?.value === ''
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Value is required for ${stage}`,
            path: ['emissionFactors', stage, 'value'],
          });
        } else if (typeof factor.value === 'string') {
          const num = Number(factor.value);
          if (Number.isNaN(num)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Value must be a valid number for ${stage}`,
              path: ['emissionFactors', stage, 'value'],
            });
          }
        }

        // Validate source when method is custom
        if (
          factor?.method === 'custom' &&
          (!factor.source || factor.source.trim() === '')
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Source is required for ${stage} when method is custom`,
            path: ['emissionFactors', stage, 'source'],
          });
        }
      }
    }
  }

  if (data.modelledAs === SoldGoodModelledAsEnum.product) {
    if (!data.linkedItemUuid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please select a product',
        path: ['linkedItemUuid'],
      });
    }
  }

  if (data.modelledAs === SoldGoodModelledAsEnum.purchased_good) {
    if (!data.linkedItemUuid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please select a purchased good',
        path: ['linkedItemUuid'],
      });
    }
  }

  if (data.modelledAs === SoldGoodModelledAsEnum.external_emission_factor) {
    // ensure all emission factors are provided with value and source
    if (!data.emissionFactors) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please provide emission factors',
        path: ['emissionFactors'],
      });
    } else {
      for (const [stage, factor] of Object.entries(data.emissionFactors)) {
        if (
          factor?.value === undefined ||
          factor?.value === null ||
          factor?.value === ''
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Value is required for ${stage}`,
            path: ['emissionFactors', stage, 'value'],
          });
        } else if (typeof factor.value === 'string') {
          const num = Number(factor.value);
          if (Number.isNaN(num)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Value must be a valid number for ${stage}`,
              path: ['emissionFactors', stage, 'value'],
            });
          }
        }

        if (!factor?.source || factor?.source.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Source is required for ${stage} when adding external emission factors`,
            path: ['emissionFactors', stage, 'source'],
          });
        }
      }
    }
  }

  if (data.modelledAs === SoldGoodModelledAsEnum.modelled_food) {
    if (!data.ingredients || data.ingredients.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please add at least one ingredient',
        path: ['ingredients'],
      });
    }

    // loop through all ingredients and ensure entityUuid, quantity, and unitUuid are provided
    data.ingredients?.forEach((ingredient, index) => {
      if (!ingredient.entityUuid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select an ingredient',
          path: ['ingredients', index, 'entityUuid'],
        });
      }
      if (!ingredient.quantity || ingredient.quantity <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please enter a valid quantity',
          path: ['ingredients', index, 'quantity'],
        });
      }
      if (!ingredient.unitUuid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select a unit',
          path: ['ingredients', index, 'unitUuid'],
        });
      }
    });
  }
});
